import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmDialog } from '../components/ConfirmDialog';
import api from '../utils/api';
import { ContentProfileModal } from './ContentPage';
import UserListModal from '../components/UserListModal';
import '../pages/Content.css';
import './Profile.css';

// ---------------------------------------------------------------------------
// Password validation
// ---------------------------------------------------------------------------
function validatePassword(password: string): string | null {
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least 1 uppercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain at least 1 number';
  return null;
}

interface Media {
  id: number;
  media_type: 'photo' | 'video';
  file_name: string;
  file_url: string;
  caption?: string | null;
  created_at: string;
  is_collab?: boolean;
  collaborator_role?: string | null;
  owner_user_id?: number | null;
  owner_name?: string | null;
}

interface Comment {
  id: number;
  media_id: number;
  comment_text: string;
  user_id: number;
  user_name: string;
  user_dp: string | null;
  like_count: number;
  liked_by_me: boolean;
  created_at: string;
}

interface Liker {
  id: number;
  name: string;
  profile_picture: string | null;
  liked_at: string;
}

interface Collaborator {
  id: number;
  user_id: number;
  user_name: string;
  user_dp: string | null;
  role_name: string;
  created_at: string;
}

interface CollaboratorForm {
  targetUserId: string;
  roleName: string;
}

interface UserSearchResult {
  id: number;
  name: string;
  email: string;
  profile_picture: string | null;
}

const ROLES = [
  'Actor', 'Audience', 'Director', 'Writer', 'Cameraman', 'Photographer', 'Editor', 'Producer',
  'Cinematographer', 'Sound Designer', 'Music Composer', 'Costume Designer',
  'Makeup Artist', 'Production Designer', 'Art Director', 'VFX Artist',
  'Stunt Coordinator', 'Casting Director', 'Line Producer', 'Assistant Director',
  'Gaffer', 'Grip', 'Other',
];

const ROLE_OPTIONS = [...ROLES];

// ─────────────────────────────────────────────────────────────
// Confirm Modal
// ─────────────────────────────────────────────────────────────

interface ConfirmModalProps {
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  message, confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  danger = false, onConfirm, onCancel,
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onConfirm, onCancel]);

  return (
    <div className="media-modal-overlay" onClick={onCancel}>
      <div className="paragone-confirm-modal" onClick={e => e.stopPropagation()}>
        <p className="paragone-confirm-message">{message}</p>
        <div className="paragone-confirm-actions">
          <button className="cancel-btn" onClick={onCancel}>{cancelLabel}</button>
          <button
            className={danger ? 'paragone-confirm-danger-btn' : 'save-btn'}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// useConfirm hook
// ─────────────────────────────────────────────────────────────

type ConfirmOptions = {
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

function useConfirm() {
  const [pending, setPending] = useState<{
    options: ConfirmOptions;
    resolve: (v: boolean) => void;
  } | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise(resolve => { setPending({ options, resolve }); });
  }, []);

  const handleConfirm = () => { pending?.resolve(true);  setPending(null); };
  const handleCancel  = () => { pending?.resolve(false); setPending(null); };

  const ConfirmDialog = pending ? (
    <ConfirmModal
      message={pending.options.message}
      confirmLabel={pending.options.confirmLabel}
      cancelLabel={pending.options.cancelLabel}
      danger={pending.options.danger}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  ) : null;

  return { confirm, ConfirmDialog };
}

// ─────────────────────────────────────────────────────────────
// Caption Modal
// ─────────────────────────────────────────────────────────────

interface CaptionModalProps {
  mediaType: 'photo' | 'video';
  onConfirm: (caption: string) => void;
  onCancel: () => void;
}

const CaptionModal: React.FC<CaptionModalProps> = ({ mediaType, onConfirm, onCancel }) => {
  const [caption, setCaption] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm(caption);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [caption, onCancel, onConfirm]);

  return (
    <div className="media-modal-overlay" onClick={onCancel}>
      <div className="caption-modal-inner" onClick={e => e.stopPropagation()}>
        <div className="caption-modal-header">
          <h3 className="caption-modal-title">
            Add Caption
            <span className="caption-modal-sub"> — {mediaType}</span>
          </h3>
        </div>
        <div className="caption-modal-body">
          <div className="form-group">
            <label>Caption <span className="caption-optional">(optional)</span></label>
            <input
              type="text"
              autoFocus
              className="caption-input"
              placeholder="Describe this work…"
              value={caption}
              onChange={e => setCaption(e.target.value)}
              maxLength={300}
            />
            <span className="caption-char-count">{caption.length}/300</span>
          </div>
        </div>
        <div className="caption-modal-actions">
          <button className="cancel-btn" onClick={onCancel}>Skip</button>
          <button className="save-btn" onClick={() => onConfirm(caption)}>Upload</button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Media Preview Modal
// ─────────────────────────────────────────────────────────────

interface MediaPreviewModalProps {
  media: Media;
  isOwner: boolean;
  currentUserId: number;
  onClose: () => void;
}

const MediaPreviewModal: React.FC<MediaPreviewModalProps> = ({
  media, isOwner, currentUserId, onClose,
}) => {
  const { confirm, confirmDialog } = useConfirmDialog();
  const [likeCount, setLikeCount]   = useState(0);
  const [likedByMe, setLikedByMe]   = useState(false);
  const [showLikers, setShowLikers] = useState(false);
  const [likers, setLikers]         = useState<Liker[]>([]);
  const [likersLoading, setLikersLoading] = useState(false);
  const [comments, setComments]     = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showLikers) { setShowLikers(false); return; }
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose, showLikers]);

  useEffect(() => { loadData(); }, [media.id]);

  const resizeComposer = useCallback(() => {
    const textarea = composerRef.current;
    if (!textarea) return;
    textarea.style.height = '0px';
    const nextHeight = Math.min(Math.max(textarea.scrollHeight, 48), 160);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > 160 ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    resizeComposer();
  }, [newComment, resizeComposer]);

  const loadData = async () => {
    try {
      setLoadingComments(true);
      const [summaryRes, commentsRes] = await Promise.all([
        api.get(`/media/${media.id}/likes-summary`),
        api.get(`/media/${media.id}/comments`),
      ]);
      setLikeCount(summaryRes.data.like_count);
      setLikedByMe(summaryRes.data.liked_by_me);
      setComments(commentsRes.data.comments);
    } catch (err) {
      console.error('Failed to load media data', err);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleLike = async () => {
    try {
      const res = await api.post(`/media/${media.id}/like`);
      setLikedByMe(res.data.liked);
      setLikeCount(res.data.like_count);
    } catch (err) { console.error('Like failed', err); }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim() || submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/media/${media.id}/comments`, { commentText: newComment.trim() });
      setComments(prev => [...prev, res.data.comment]);
      setNewComment('');
    } catch (err) { console.error('Comment failed', err); }
    finally { setSubmitting(false); }
  };

  const handleDeleteComment = async (commentId: number) => {
    const approved = await confirm({
      title: 'Delete Comment',
      message: 'Delete this comment?',
      confirmLabel: 'Delete',
      cancelLabel: 'Keep',
      danger: true,
    });
    if (!approved) return;
    try {
      await api.delete(`/media/${media.id}/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (err) { console.error('Delete comment failed', err); }
  };

  const handleToggleCommentLike = async (commentId: number) => {
    try {
      const res = await api.post(`/media/${media.id}/comments/${commentId}/like`);
      setComments(prev => prev.map(c =>
        c.id === commentId
          ? { ...c, liked_by_me: res.data.liked, like_count: res.data.like_count }
          : c
      ));
    } catch (err) { console.error('Comment like failed', err); }
  };

  const handleShowLikers = async () => {
    setShowLikers(true);
    setLikersLoading(true);
    try {
      const res = await api.get(`/media/${media.id}/likes`);
      setLikers(res.data.likers || []);
    } catch (err) {
      console.error('Failed to load likers', err);
    } finally {
      setLikersLoading(false);
    }
  };

  const src = media.file_url;

  return (
    <>
      {confirmDialog}
      <div className="media-modal-overlay" onClick={onClose}>
        <div className="media-preview-modal" onClick={e => e.stopPropagation()}>
        <button className="media-modal-close" onClick={onClose} aria-label="Close">×</button>

        {/* ── Likers overlay — floats on top of the preview modal ── */}
        {showLikers && (
          <div className="mpm-likers-overlay" onClick={() => setShowLikers(false)}>
            <div className="mpm-likers-modal" onClick={e => e.stopPropagation()}>
              <div className="mpm-likers-modal-header">
                <span className="mpm-likers-modal-title">Liked by</span>
                <button
                  className="mpm-likers-modal-close"
                  onClick={() => setShowLikers(false)}
                  aria-label="Close likers"
                >×</button>
              </div>
              <div className="mpm-likers-modal-body">
                {likersLoading ? (
                  <div className="mpm-comments-loading">Loading…</div>
                ) : likers.length === 0 ? (
                  <p className="mpm-no-comments">No likes yet.</p>
                ) : (
                  likers.map(liker => (
                    <div key={liker.id} className="mpm-liker-row">
                      <div className="mpm-liker-avatar">
                        {liker.profile_picture
                          ? <img src={liker.profile_picture} alt={liker.name} />
                          : <span>{liker.name.charAt(0).toUpperCase()}</span>
                        }
                      </div>
                      <span className="mpm-liker-name">{liker.name}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mpm-media-col">
          {media.media_type === 'video' ? (
            <video ref={videoRef} className="mpm-media-el" controls autoPlay playsInline src={src} />
          ) : (
            <img className="mpm-media-el" src={src} alt="" />
          )}
        </div>

        <div className="mpm-info-col">
          {media.caption && <p className="mpm-caption">{media.caption}</p>}

          <div className="mpm-like-bar">
            <button
              className={`mpm-like-btn ${likedByMe ? 'mpm-like-btn--active' : ''}`}
              onClick={handleLike}
              aria-label={likedByMe ? 'Unlike' : 'Like'}
            >
              <span className="mpm-heart-icon">{likedByMe ? '\u2665' : '\u2661'}</span>
            </button>
            <button className="mpm-like-count-btn" onClick={handleShowLikers}>
              {likeCount} like{likeCount === 1 ? '' : 's'}
            </button>
            <span className="mpm-comment-count">
              {comments.length} comment{comments.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="mpm-comments">
            {loadingComments ? (
              <div className="mpm-comments-loading">Loading…</div>
            ) : comments.length === 0 ? (
              <p className="mpm-no-comments">No comments yet. Be the first!</p>
            ) : (
              comments.map(c => (
                <div key={c.id} className="mpm-comment">
                  <div className="mpm-comment-avatar">
                    {c.user_dp
                      ? <img src={c.user_dp} alt={c.user_name} />
                      : <span>{c.user_name?.charAt(0).toUpperCase()}</span>
                    }
                  </div>
                  <div className="mpm-comment-body">
                    <span className="mpm-comment-author">{c.user_name}</span>
                    <span className="mpm-comment-text">{c.comment_text}</span>
                  </div>
                  <div className="mpm-comment-actions">
                    <button
                      className={`mpm-clike-btn ${c.liked_by_me ? 'mpm-clike-btn--active' : ''}`}
                      onClick={() => handleToggleCommentLike(c.id)}
                    >
                      {c.liked_by_me ? '♥' : '♡'} {c.like_count > 0 ? c.like_count : ''}
                    </button>
                    {(isOwner || c.user_id === currentUserId) && (
                      <button className="mpm-cdel-btn" onClick={() => handleDeleteComment(c.id)}>×</button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="mpm-comment-input-row">
            <textarea
              ref={composerRef}
              className="mpm-comment-input"
              placeholder="Add a comment…"
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              onInput={resizeComposer}
              maxLength={500}
              rows={1}
            />
            <button
              className="mpm-comment-submit"
              onClick={handleSubmitComment}
              disabled={!newComment.trim() || submitting}
            >
              {submitting ? '…' : 'Post'}
            </button>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Helper: detect if a role value is a custom "Other" role
// ─────────────────────────────────────────────────────────────
const PREDEFINED_ROLES = new Set(ROLES);

function parseRolesForEdit(savedRoles: string[]): {
  selectedRoles: string[];
  customRoleValue: string;
} {
  let customRoleValue = '';
  const selectedRoles: string[] = [];

  for (const role of savedRoles) {
    if (PREDEFINED_ROLES.has(role)) {
      selectedRoles.push(role);
    } else {
      customRoleValue = role;
      selectedRoles.push('Other');
    }
  }

  return { selectedRoles, customRoleValue };
}

// ─────────────────────────────────────────────────────────────
// Main ProfilePage
// ─────────────────────────────────────────────────────────────

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const dpInputRef    = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const { confirm, ConfirmDialog } = useConfirm();

  const [profile, setProfile]               = useState<any>(null);
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [photos, setPhotos]                 = useState<Media[]>([]);
  const [videos, setVideos]                 = useState<Media[]>([]);
  const [editMode, setEditMode]             = useState(false);
  const [editedProfile, setEditedProfile]   = useState<any>({});
  const [editCustomRole, setEditCustomRole] = useState('');
  const [loading, setLoading]               = useState(true);
  const [uploading, setUploading]           = useState(false);
  const [error, setError]                   = useState('');
  const [message, setMessage]               = useState('');
  const [viewMediaType, setViewMediaType]   = useState<'photos' | 'videos'>('photos');
  const [isGoogleUser, setIsGoogleUser]     = useState(false);
  const [changePasswordMode, setChangePasswordMode] = useState(false);
  const [otpSent, setOtpSent]               = useState(false);
  const [sendOtpLoading, setSendOtpLoading] = useState(false);
  const [passwordData, setPasswordData]     = useState({ otp: '', newPassword: '', confirmPassword: '' });
  const [profileListType, setProfileListType] = useState<'connections' | 'followers' | null>(null);
  const [profileListLoading, setProfileListLoading] = useState(false);
  const [profileListUsers, setProfileListUsers] = useState<UserSearchResult[]>([]);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const [captionModal, setCaptionModal]     = useState<{ file: File; type: 'photo' | 'video' } | null>(null);
  const [previewMedia, setPreviewMedia]     = useState<Media | null>(null);

  const [showCollabModal, setShowCollabModal]   = useState(false);
  const [selectedMediaId, setSelectedMediaId]   = useState<number | null>(null);
  const [collaborators, setCollaborators]       = useState<Collaborator[]>([]);
  const [collabForm, setCollabForm]             = useState<CollaboratorForm>({ targetUserId: '', roleName: '' });
  const [loadingCollaborators, setLoadingCollaborators] = useState(false);
  const [connectedUsers, setConnectedUsers]     = useState<UserSearchResult[]>([]);
  const [collabSearchText, setCollabSearchText] = useState('');
  const [selectedUser, setSelectedUser]         = useState<UserSearchResult | null>(null);
  const queryMediaId = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('mediaId');
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : null;
  }, [location.search]);

  useEffect(() => { loadProfile(); loadMedia(); checkAuthProvider(); }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPreviewMedia(null);
        const params = new URLSearchParams(location.search);
        if (params.has('mediaId')) {
          params.delete('mediaId');
          const nextSearch = params.toString();
          navigate(
            {
              pathname: '/profile',
              search: nextSearch ? `?${nextSearch}` : '',
            },
            { replace: true },
          );
        }
        closeCollabModal();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [location.search, navigate]);

  useEffect(() => {
    document.body.style.overflow = showCollabModal || !!captionModal ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showCollabModal, captionModal]);

  const clearProfileMediaQuery = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('mediaId')) return;
    params.delete('mediaId');
    const nextSearch = params.toString();
    navigate(
      {
        pathname: '/profile',
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true },
    );
  }, [location.search, navigate]);

  const closePreviewMedia = useCallback(() => {
    setPreviewMedia(null);
    clearProfileMediaQuery();
  }, [clearProfileMediaQuery]);

  const closeCollabModal = () => {
    setShowCollabModal(false);
    setCollabSearchText('');
    setSelectedUser(null);
    setCollabForm({ targetUserId: '', roleName: '' });
  };

  const loadProfileUserList = useCallback(async (type: 'connections' | 'followers') => {
    setProfileListType(type);
    setProfileListLoading(true);
    setProfileListUsers([]);

    try {
      const res = await api.get(`/user/${user?.id}/` + type);
      setProfileListUsers(res.data.users || []);
    } catch (err) {
      console.error(`Failed to load ${type}:`, err);
      setProfileListUsers([]);
    } finally {
      setProfileListLoading(false);
    }
  }, [user?.id]);

  const closeProfileUserList = () => {
    setProfileListType(null);
    setProfileListLoading(false);
    setProfileListUsers([]);
  };


  const showError   = (msg: string) => { setError(msg);   setTimeout(() => setError(''),   3000); };
  const showMessage = (msg: string) => { setMessage(msg); setTimeout(() => setMessage(''), 2500); };

  const hydrateEditProfile = useCallback((nextProfile: any) => {
    const savedRoles: string[] = nextProfile?.roles || [];
    const { selectedRoles, customRoleValue } = parseRolesForEdit(savedRoles);

    setEditedProfile({
      name: nextProfile?.name || '',
      email: nextProfile?.email || '',
      address: nextProfile?.address || '',
      city: nextProfile?.city || '',
      state: nextProfile?.state || '',
      pincode: nextProfile?.pincode || '',
      roles: selectedRoles,
    });
    setEditCustomRole(customRoleValue);
  }, []);

  const loadProfile = async () => {
    try {
      const res = await api.get('/user/profile');
      setProfile(res.data.user);
      hydrateEditProfile(res.data.user);
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadMedia = async () => {
    if (!user?.id) return;
    try {
      const res = await api.get(`/media/${user.id}`);
      setPhotos(res.data.photos);
      setVideos(res.data.videos);
      setProfilePicture(res.data.profilePicture ?? null);
    } catch (err) {
      console.error('Failed to load media:', err);
    }
  };

  useEffect(() => {
    if (!queryMediaId || previewMedia?.id === queryMediaId) return;

    const localMedia = [...photos, ...videos].find((item) => item.id === queryMediaId);
    if (localMedia) {
      setViewMediaType(localMedia.media_type === 'video' ? 'videos' : 'photos');
      setPreviewMedia(localMedia);
      return;
    }

    api
      .get(`/media/item/${queryMediaId}`)
      .then((res) => {
        const media = res.data.media as Media;
        setViewMediaType(media.media_type === 'video' ? 'videos' : 'photos');
        setPreviewMedia(media);
      })
      .catch(() => {});
  }, [photos, previewMedia?.id, queryMediaId, videos]);

  const checkAuthProvider = async () => {
    try {
      const res = await api.get('/user/auth-provider');
      setIsGoogleUser(res.data.authProvider === 'google');
    } catch { /* silent */ }
  };

  const loadConnectedUsers = async () => {
    try {
      const res = await api.get('/user/connected');
      setConnectedUsers(res.data.users || []);
    } catch (err) { console.error('Failed to load connected users:', err); }
  };

  const filteredUsers: UserSearchResult[] = collabSearchText.length > 0
    ? connectedUsers.filter(u =>
        `${u.name} ${u.email}`.toLowerCase().includes(collabSearchText.toLowerCase())
      )
    : [];

  // ── Collaborator handlers ──────────────────────────────────

  const loadCollaborators = async (mediaId: number) => {
    try {
      setLoadingCollaborators(true);
      const res = await api.get(`/media/${mediaId}/collaborators`);
      setCollaborators(res.data.collaborators);
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to load collaborators');
    } finally {
      setLoadingCollaborators(false);
    }
  };

  const addCollaborator = async () => {
    if (!selectedMediaId || !collabForm.targetUserId || !collabForm.roleName.trim()) {
      showError('Please select a user and role'); return;
    }
    try {
      await api.post(`/media/${selectedMediaId}/collaborators`, {
        targetUserId: parseInt(collabForm.targetUserId),
        roleName: collabForm.roleName.trim(),
      });
      showMessage('Collaborator added');
      setCollabForm({ targetUserId: '', roleName: '' });
      setSelectedUser(null);
      setCollabSearchText('');
      if (selectedMediaId) await loadCollaborators(selectedMediaId);
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to add collaborator');
    }
  };

  const removeCollaborator = async (collabId: number) => {
    const ok = await confirm({
      message: 'Remove this collaborator?',
      confirmLabel: 'Remove', cancelLabel: 'Cancel', danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/media/${selectedMediaId}/collaborators/${collabId}`);
      showMessage('Collaborator removed');
      if (selectedMediaId) await loadCollaborators(selectedMediaId);
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to remove collaborator');
    }
  };

  // ── Upload handlers ────────────────────────────────────────

  const handleProfilePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showError('Please upload an image file'); return; }
    if (file.size > 100 * 1024 * 1024)    { showError('Image must be under 100 MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await api.post('/media/upload-dp', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await api.post('/media/profile-picture', { fileName: uploadRes.data.key, fileSize: file.size });
      showMessage('Profile picture updated');
      loadMedia();
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to upload profile picture');
    } finally {
      setUploading(false);
      if (dpInputRef.current) dpInputRef.current.value = '';
    }
  };

  const handleMediaFileChosen = async (e: React.ChangeEvent<HTMLInputElement>, type: 'photo' | 'video') => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (type === 'photo' && !file.type.startsWith('image/')) { showError('Please upload an image file'); return; }
    if (type === 'video' && !file.type.startsWith('video/')) { showError('Please upload a video file'); return; }
    if (type === 'photo' && file.size > 100 * 1024 * 1024)    { showError('Image must be under 100 MB'); return; }
    if (type === 'video' && file.size > 100 * 1024 * 1024)  { showError('Video must be under 100 MB'); return; }
    setCaptionModal({ file, type });
    if (photoInputRef.current) photoInputRef.current.value = '';
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const doUploadMedia = async (caption: string) => {
    if (!captionModal) return;
    const { file, type } = captionModal;
    setCaptionModal(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (caption.trim()) formData.append('caption', caption.trim());
      await api.post('/media/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      showMessage(`${type === 'photo' ? 'Photo' : 'Video'} added`);
      loadMedia();
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDP = async () => {
    const ok = await confirm({
      message: 'Remove your profile picture?',
      confirmLabel: 'Remove', cancelLabel: 'Cancel', danger: true,
    });
    if (!ok) return;
    try {
      await api.delete('/media/profile-picture/remove');
      showMessage('Profile picture removed');
      loadMedia();
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to remove profile picture');
    }
  };

  const handleDeleteMedia = async (mediaId: number) => {
    const ok = await confirm({
      message: 'Delete this item? This cannot be undone.',
      confirmLabel: 'Delete', cancelLabel: 'Cancel', danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/media/${mediaId}`);
      showMessage('Deleted');
      loadMedia();
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to delete');
    }
  };

  const handleStartEdit = () => {
    hydrateEditProfile(profile);
    setEditMode(true);
  };

  const handleEditRoleToggle = (role: string) => {
    const currentRoles: string[] = editedProfile.roles || [];

    if (role === 'Other') {
      if (currentRoles.includes('Other')) {
        setEditedProfile({ ...editedProfile, roles: currentRoles.filter((r: string) => r !== 'Other') });
        setEditCustomRole('');
      } else {
        if (currentRoles.length >= 5) { showError('Maximum 5 roles allowed'); return; }
        setEditedProfile({ ...editedProfile, roles: [...currentRoles, 'Other'] });
      }
      return;
    }

    if (currentRoles.includes(role)) {
      setEditedProfile({ ...editedProfile, roles: currentRoles.filter((r: string) => r !== role) });
    } else {
      if (currentRoles.length >= 5) { showError('Maximum 5 roles allowed'); return; }
      setEditedProfile({ ...editedProfile, roles: [...currentRoles, role] });
    }
  };

  const handleSaveProfile = async () => {
    let finalRoles: string[] = editedProfile.roles || [];
    if (finalRoles.includes('Other')) {
      if (!editCustomRole.trim()) { showError('Please specify your custom role'); return; }
      finalRoles = finalRoles.map((r: string) => r === 'Other' ? editCustomRole.trim() : r);
    }

    try {
      await api.put('/user/profile', { ...editedProfile, roles: finalRoles });
      showMessage('Profile updated');
      setEditMode(false);
      loadProfile();
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to update profile');
    }
  };

  const handleCancelEdit = () => {
    hydrateEditProfile(profile);
    setEditMode(false);
  };

  const handleDeleteAccount = async () => {
    const ok = await confirm({
      message: 'Delete your account? This action cannot be undone and all your data will be permanently removed.',
      confirmLabel: 'Delete Account', cancelLabel: 'Cancel', danger: true,
    });
    if (!ok) return;
    try {
      await api.delete('/user/delete-account');
      showMessage('Account deleted successfully');
      setTimeout(() => { logout(); navigate('/'); }, 1500);
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to delete account');
    }
  };

  const handleSendChangePasswordOTP = async () => {
    setSendOtpLoading(true);
    try {
      await api.post('/user/send-change-password-otp');
      setOtpSent(true);
      showMessage('OTP sent to your email');
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setSendOtpLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.otp || passwordData.otp.length !== 6) {
      showError('Please enter the 6-digit OTP'); return;
    }
    const pwError = validatePassword(passwordData.newPassword);
    if (pwError) { showError(pwError); return; }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showError('Passwords do not match'); return;
    }
    try {
      await api.post('/user/change-password', {
        otp: passwordData.otp,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword,
      });
      showMessage('Password changed successfully. Please log in again.');
      setChangePasswordMode(false); setOtpSent(false); setSendOtpLoading(false);
      setPasswordData({ otp: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => logout(), 2000);
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to change password');
    }
  };

  const renderChangePasswordHint = () => {
    const pw = passwordData.newPassword;
    if (!pw) return null;
    const checks = [
      { label: '8+ characters',     ok: pw.length >= 8 },
      { label: '1 uppercase letter', ok: /[A-Z]/.test(pw) },
      { label: '1 number',           ok: /[0-9]/.test(pw) },
    ];
    const allPassed = checks.every(c => c.ok);
    if (allPassed) return null;
    return (
      <ul className="password-hint-list">
        {checks.map(c => (
          <li key={c.label} className={c.ok ? 'hint-ok' : 'hint-fail'}>
            {c.ok ? '✓' : '✗'} {c.label}
          </li>
        ))}
      </ul>
    );
  };

  if (loading) {
    return <div className="profile-container"><div className="loading">Loading profile...</div></div>;
  }

  return (
    <div className="profile-container">

      {ConfirmDialog}

      {captionModal && (
        <CaptionModal
          mediaType={captionModal.type}
          onConfirm={doUploadMedia}
          onCancel={() => setCaptionModal(null)}
        />
      )}

      {previewMedia && (
        <MediaPreviewModal
          media={previewMedia}
          isOwner={true}
          currentUserId={user?.id ?? 0}
          onClose={closePreviewMedia}
        />
      )}

      {/* Collaborator Modal */}
      {showCollabModal && selectedMediaId && (
        <div className="collab-modal-overlay" onClick={closeCollabModal}>
          <div className="collab-modal-inner" onClick={e => e.stopPropagation()}>
            <div className="collab-modal-header">
              <h3>Collaborators</h3>
              <button className="media-modal-close" onClick={closeCollabModal} aria-label="Close">×</button>
            </div>

            <div className="collab-form-section">
              <div className="form-group">
                <label>User</label>
                <input
                  type="text"
                  className="user-search-input"
                  placeholder="Type name or email to filter connected users..."
                  value={selectedUser ? `${selectedUser.name} — ${selectedUser.email}` : collabSearchText}
                  onChange={e => {
                    setCollabSearchText(e.target.value);
                    setSelectedUser(null);
                    setCollabForm({ ...collabForm, targetUserId: '' });
                  }}
                />
                {!selectedUser && collabSearchText.length > 0 && (
                  <div className="search-results">
                    {filteredUsers.length > 0 ? filteredUsers.map(u => (
                      <div key={u.id} className="search-result-item" onClick={() => {
                        setSelectedUser(u);
                        setCollabForm({ ...collabForm, targetUserId: u.id.toString() });
                        setCollabSearchText('');
                      }}>
                        <div className="user-avatar">
                          {u.profile_picture
                            ? <img src={u.profile_picture} alt={u.name} />
                            : <div className="user-initial">{u.name?.charAt(0).toUpperCase()}</div>
                          }
                        </div>
                        <div className="user-info">
                          <div className="user-name">{u.name}</div>
                          <div className="user-email">{u.email}</div>
                        </div>
                      </div>
                    )) : (
                      <div className="search-result-item" style={{ color: 'var(--ink-muted)', cursor: 'default' }}>
                        No connected users match
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Role</label>
                <div className="role-combobox">
                  <input
                    type="text"
                    value={collabForm.roleName}
                    placeholder="Select or type role..."
                    list="role-suggestions"
                    onChange={e => setCollabForm({ ...collabForm, roleName: e.target.value })}
                    className="role-input"
                  />
                  <datalist id="role-suggestions">
                    {ROLE_OPTIONS.map(role => <option key={role} value={role} />)}
                  </datalist>
                </div>
              </div>
              <button
                onClick={addCollaborator}
                className="add-collab-btn"
                disabled={!selectedMediaId || !collabForm.targetUserId || !collabForm.roleName.trim()}
              >Add Collaborator</button>
            </div>

            <div className="collab-list-section">
              <h4>Current Collaborators {loadingCollaborators && <span className="loading-dots">...</span>}</h4>
              {collaborators.length === 0 ? (
                <div className="no-collabs">No collaborators yet</div>
              ) : (
                <div className="collaborators-grid">
                  {collaborators.map(collab => (
                    <div key={collab.id} className="collab-item">
                      <div className="collab-avatar">
                        {collab.user_dp
                          ? <img src={collab.user_dp} alt={collab.user_name} />
                          : <div>{collab.user_name.charAt(0).toUpperCase()}</div>
                        }
                      </div>
                      <div className="collab-info">
                        <div className="collab-name">{collab.user_name}</div>
                        <div className="collab-role">{collab.role_name}</div>
                      </div>
                      <button className="remove-collab-btn" onClick={() => removeCollaborator(collab.id)}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="profile-header">
        <button onClick={() => navigate('/content')} className="back-btn">← Home</button>
        <h1>My Profile</h1>
      </div>

      {(error || message) && (
        <div className="profile-messages">
          {error   && <div className="error-message">{error}</div>}
          {message && <div className="success-message">{message}</div>}
        </div>
      )}

      <div className="profile-content">

        {/* Profile Picture */}
        <div className="profile-dp-section">
          <div className="dp-container">
            {profilePicture ? (
              <img src={profilePicture} alt="Profile" className="profile-dp-large" />
            ) : (
              <div className="profile-dp-placeholder">
                {profile?.name?.charAt(0).toUpperCase() || '?'}
              </div>
            )}
          </div>
          <div className="dp-meta">
            <h2>{profile?.name || 'Your Name'}</h2>
            <p>{profile?.email}</p>
            <div className="profile-counts">
              <button type="button" className="profile-count-btn" onClick={() => loadProfileUserList('connections')}>
                <strong>{profile?.connectionCount ?? 0}</strong> Connections
              </button>
              <button type="button" className="profile-count-btn" onClick={() => loadProfileUserList('followers')}>
                <strong>{profile?.followerCount ?? 0}</strong> Followers
              </button>
            </div>
            <div className="dp-actions">
              <button onClick={() => setShowProfileModal(true)} className="share-profile-btn">
                Share Profile
              </button>
              <input type="file" ref={dpInputRef} onChange={handleProfilePictureUpload} accept="image/*" style={{ display: 'none' }} />
              <button onClick={() => dpInputRef.current?.click()} className="upload-dp-btn" disabled={uploading}>
                {uploading ? 'Uploading...' : profilePicture ? 'Change Photo' : 'Upload Photo'}
              </button>
              {profilePicture && (
                <button onClick={handleDeleteDP} className="remove-dp-btn" disabled={uploading}>Remove</button>
              )}
            </div>
          </div>
        </div>

        {profileListType && (
          <UserListModal
            title={profileListType === 'connections' ? 'Connections' : 'Followers'}
            users={profileListUsers}
            loading={profileListLoading}
            onClose={closeProfileUserList}
          />
        )}

        {showProfileModal && profile?.id && (
          <ContentProfileModal
            userId={profile.id}
            onClose={() => setShowProfileModal(false)}
            canInteract={true}
            onAuthRequired={() => {}}
          />
        )}

        {/* Media Gallery */}
        <div className="media-gallery-section">
          <div className="media-header">
            <h2>Media Gallery</h2>
            <div className="media-tabs">
              <button className={`media-tab ${viewMediaType === 'photos' ? 'active' : ''}`} onClick={() => setViewMediaType('photos')}>
                Photos ({photos.length})
              </button>
              <button className={`media-tab ${viewMediaType === 'videos' ? 'active' : ''}`} onClick={() => setViewMediaType('videos')}>
                Videos ({videos.length})
              </button>
            </div>
          </div>

          <div className="media-content">
            <input type="file" ref={photoInputRef} onChange={e => handleMediaFileChosen(e, 'photo')} accept="image/*" style={{ display: 'none' }} />
            <input type="file" ref={videoInputRef} onChange={e => handleMediaFileChosen(e, 'video')} accept="video/*" style={{ display: 'none' }} />
            <button
              onClick={() => (viewMediaType === 'photos' ? photoInputRef : videoInputRef).current?.click()}
              className="add-media-btn"
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : `+ Add ${viewMediaType === 'photos' ? 'Photo' : 'Video'}`}
            </button>

            {(viewMediaType === 'photos' ? photos : videos).length === 0 ? (
              <div className="no-media">
                <p>No {viewMediaType}s yet. Add your first {viewMediaType}.</p>
              </div>
            ) : (
              <div className="media-grid">
                {(viewMediaType === 'photos' ? photos : videos).map(media => (
                  <div key={media.id} className="media-item-wrapper">
                    <div className="media-item" onClick={() => setPreviewMedia(media)}>
                      {viewMediaType === 'photos' ? (
                        <img src={media.file_url} alt="" loading="lazy" />
                      ) : (
                        <>
                          <video src={`${media.file_url}#t=0.5`} preload="metadata" muted playsInline />
                          <div className="video-play-icon">▶</div>
                        </>
                      )}
                      {!media.is_collab && (
                        <button
                          className="delete-media-btn"
                          onClick={e => { e.stopPropagation(); handleDeleteMedia(media.id); }}
                          aria-label="Delete media"
                        >×</button>
                      )}
                    </div>

                    {media.caption && (
                      <p className="media-caption-preview">{media.caption}</p>
                    )}

                    {media.is_collab ? (
                      <div className="collab-post-badge">
                        Tagged as {media.collaborator_role || 'Collaborator'}
                        {media.owner_name ? ` by ${media.owner_name}` : ''}
                      </div>
                    ) : (
                      <button
                        className="collab-toggle-btn"
                        title="Manage Collaborators"
                        onClick={e => {
                          e.stopPropagation();
                          setSelectedMediaId(media.id);
                          setCollabSearchText('');
                          setSelectedUser(null);
                          setCollabForm({ targetUserId: '', roleName: '' });
                          setShowCollabModal(true);
                          loadCollaborators(media.id);
                          loadConnectedUsers();
                        }}
                      >
                        👥 Collaborators
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Personal Information */}
        <div className="profile-info-section">
          <div className="profile-info-header">
            <h2>Personal Information</h2>
            {!editMode && !changePasswordMode && (
              <button onClick={handleStartEdit} className="edit-profile-btn">Edit Profile</button>
            )}
          </div>

          {editMode && (
            <div className="profile-edit-form">
              <div className="form-group">
                <label>Name</label>
                <input type="text" value={editedProfile.name || ''} placeholder="Full name"
                  onChange={e => setEditedProfile({ ...editedProfile, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="text" value={editedProfile.email || ''} disabled />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input type="text" value={editedProfile.address || ''} placeholder="Street address"
                  onChange={e => setEditedProfile({ ...editedProfile, address: e.target.value })} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input type="text" value={editedProfile.city || ''} placeholder="City"
                    onChange={e => setEditedProfile({ ...editedProfile, city: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input type="text" value={editedProfile.state || ''} placeholder="State"
                    onChange={e => setEditedProfile({ ...editedProfile, state: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label>Pincode</label>
                <input type="text" value={editedProfile.pincode || ''} placeholder="Pincode"
                  onChange={e => setEditedProfile({ ...editedProfile, pincode: e.target.value })} />
              </div>

              <div className="form-group">
                <label>Roles (select up to 5)</label>
                <div className="roles-grid-edit">
                  {ROLES.map(role => (
                    <button key={role} type="button"
                      className={`role-btn-small ${(editedProfile.roles || []).includes(role) ? 'selected' : ''}`}
                      onClick={() => handleEditRoleToggle(role)}
                    >
                      {role}
                      {(editedProfile.roles || []).includes(role) && <span className="checkmark"> ✓</span>}
                    </button>
                  ))}
                </div>

                {(editedProfile.roles || []).includes('Other') && (
                  <div className="custom-role-input" style={{ marginTop: '10px' }}>
                    <input
                      type="text"
                      value={editCustomRole}
                      onChange={e => setEditCustomRole(e.target.value)}
                      placeholder="Specify your role"
                      maxLength={50}
                    />
                  </div>
                )}

                {(editedProfile.roles || []).length > 0 && (
                  <div className="selected-roles-display" style={{ marginTop: '8px' }}>
                    <strong>Selected ({(editedProfile.roles || []).length}/5):</strong>{' '}
                    {(editedProfile.roles || []).map((role: string) => (
                      <span key={role} className="selected-role-tag">
                        {role === 'Other' && editCustomRole ? editCustomRole : role}
                        <button
                          type="button"
                          onClick={() => handleEditRoleToggle(role)}
                          className="remove-role-btn"
                        >×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button onClick={handleSaveProfile} className="save-btn">Save Changes</button>
                <button onClick={handleCancelEdit} className="cancel-btn">Cancel</button>
              </div>
            </div>
          )}

          {changePasswordMode && !editMode && (
            <div className="password-change-section">
              {!otpSent ? (
                <button
                  onClick={handleSendChangePasswordOTP}
                  className="send-otp-btn"
                  disabled={sendOtpLoading}
                >
                  {sendOtpLoading ? 'Sending...' : 'Send OTP to Email'}
                </button>
              ) : (
                <div className="password-form">
                  <div className="form-group">
                    <label>OTP</label>
                    <input type="text" value={passwordData.otp} placeholder="6-digit code" maxLength={6}
                      onChange={e => setPasswordData({ ...passwordData, otp: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>New Password</label>
                    <input type="password" value={passwordData.newPassword} placeholder="New password"
                      onChange={e => setPasswordData({ ...passwordData, newPassword: e.target.value })} />
                    {renderChangePasswordHint()}
                  </div>
                  <div className="form-group">
                    <label>Confirm Password</label>
                    <input type="password" value={passwordData.confirmPassword} placeholder="Repeat new password"
                      onChange={e => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} />
                  </div>
                  <div className="form-actions">
                    <button onClick={handleChangePassword} className="save-btn">Change Password</button>
                    <button onClick={() => {
                      setChangePasswordMode(false);
                      setOtpSent(false);
                      setSendOtpLoading(false);
                      setPasswordData({ otp: '', newPassword: '', confirmPassword: '' });
                    }} className="cancel-btn">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {!editMode && !changePasswordMode && (
            <div className="profile-details">
              {([
                { label: 'Name',    value: profile?.name },
                { label: 'Email',   value: profile?.email },
                { label: 'Address', value: profile?.address || 'Not provided' },
                { label: 'City',    value: profile?.city    || 'Not provided' },
                { label: 'State',   value: profile?.state   || 'Not provided' },
                { label: 'Pincode', value: profile?.pincode || 'Not provided' },
              ] as { label: string; value: string }[]).map(({ label, value }) => (
                <div className="detail-item" key={label}>
                  <span className="detail-label">{label}</span>
                  <span className="detail-value">{value}</span>
                </div>
              ))}
              <div className="detail-item">
                <span className="detail-label">Roles</span>
                <div className="detail-value roles-display">
                  {profile?.roles?.length
                    ? profile.roles.map((role: string, i: number) => (
                        <span key={i} className="role-badge">{role}</span>
                      ))
                    : <span style={{ fontStyle: 'italic', color: 'var(--ink-muted)' }}>None selected</span>
                  }
                </div>
              </div>
              <div style={{ padding: '20px 24px', background: 'var(--offwhite)' }}>
                {isGoogleUser ? (
                  <div className="google-password-note">
                    <span className="google-password-icon">G</span>
                    <span>Password change is unavailable for Google sign-in accounts.</span>
                  </div>
                ) : (
                  <button onClick={() => setChangePasswordMode(true)} className="change-password-btn">
                    Change Password
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '20px 24px' }}>
          <button onClick={handleDeleteAccount} className="delete-account-btn">
            Delete Account
          </button>
          <button onClick={logout} className="logout-btn">Logout</button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
